import styled from '@emotion/styled'
import { font, spacing } from '@mobi/component-library/Theme/Common'
import { hexColors } from '@mobi/settings/hexColors'
import React, { useState } from 'react'
import { VerificationCodeForm } from '../Settings/Components/MFASettings'
import {
  changePasswordSubmit,
  completeChangePasswordWithMfa,
  handleChangePasswordResponse,
  handleLogonMfaResult,
  MfaChallenge,
} from '../../Data/Account/changePassword'
import { observeImmutable } from '@core/Components/HOCs'
import { state$ as changePasswordState$, ChangePasswordStateRecord } from './driver'
import { HelpText } from '@core/Components/Messages/HelpText'
import { MfaCodePageText } from '@core/Areas/Settings/Components/MFASettings/Pages/VerificationCodePage.styles'
import {
  isMfaError,
  mfaCodeText,
} from '@core/Areas/Settings/Components/MFASettings/Helpers/helpers'
import { completeLogonWithMfa } from '@core/Areas/Login/helpers/api'
import { getRememberAccount } from '@core/Areas/Login/hooks/useLoginState'

export const ChangePasswordVerificationCodePageObserve = observeImmutable<
  ChangePasswordStateRecord,
  Props
>(changePasswordState$, ({ record, ...props }) => {
  const existingPassword = record.existing
  const newPassword = record.new
  const confirmPassword = record.confirm

  if (!existingPassword || !newPassword || !confirmPassword) return null

  return (
    <ChangePasswordVerificationCodePage
      {...props}
      existingPassword={existingPassword}
      newPassword={newPassword}
      confirmPassword={confirmPassword}
    />
  )
})

interface Props {
  accountNumber: string
  mfaChallenge: MfaChallenge
  isBiometricsEnabled: boolean
  isChangeTemporaryPassword: boolean | undefined
  isKeepMeLoggedIn: boolean
  form: HTMLFormElement
  onMfaChallengeReceived: (challenge: MfaChallenge | undefined) => void
  onMfaSuccessful: () => void
}

interface ChangePasswordVerficiationCodeProps extends Props {
  existingPassword: string
  newPassword: string
  confirmPassword: string
}

const ChangePasswordVerificationCodePage: React.FC<ChangePasswordVerficiationCodeProps> = ({
  accountNumber,
  mfaChallenge: { mfaSessionId, mfaFactor, mfaHint },
  isBiometricsEnabled,
  isChangeTemporaryPassword,
  isKeepMeLoggedIn: keepLoggedIn,
  form,
  existingPassword,
  newPassword,
  confirmPassword,
  onMfaChallengeReceived,
  onMfaSuccessful,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()

  const handleVerify = async (mfaCode: string) => {
    setIsLoading(true)

    try {
      if (isChangeTemporaryPassword) {
        // Password has already been updated, we just need to verify MFA to complete the login process
        const rememberAccount = getRememberAccount()
        const result = await completeLogonWithMfa({
          accountNumber: parseInt(accountNumber),
          mfaSessionId,
          mfaFactor,
          mfaCode,
          keepLoggedIn,
          rememberAccount,
        })

        const success = handleLogonMfaResult(result, { form, onSuccess: onMfaSuccessful })

        if (!success) {
          setError('Verification failed')
        }
      } else {
        const response = await completeChangePasswordWithMfa({
          accountNumber,
          mfaSessionId,
          mfaFactor,
          mfaCode,
          currentPassword: existingPassword,
          newPassword,
        })

        const success = handleChangePasswordResponse(response, {
          accountNumber,
          newPassword,
          form,
          isBiometricsEnabledByUser: isBiometricsEnabled,
          isChangeTemporaryPassword,
          isDesktop: false,
          onSuccess: onMfaSuccessful,
        })

        if (!success && response.error) {
          setError(response.error || 'Verification failed')
        }
      }
    } catch (error) {
      if (isMfaError(error)) {
        if (error.status === 403) {
          setError('Sorry the code you have provided is incorrect')
        } else {
          setError(error.message)
        }
      } else {
        setError('Verification failed')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaChallengeResend = async () => {
    setIsLoading(true)

    try {
      const response = await changePasswordSubmit(
        { accountNumber, existingPassword, newPassword, confirmPassword },
        false
      )
      if (response.mustCompleteMfaChallenge) {
        onMfaChallengeReceived({
          mfaSessionId: response.mfaSessionId,
          mfaFactor: response.mfaFactor,
          mfaHint: response.mfaHint,
        })
      } else if (response.error) {
        setError(response.error || 'Verification failed')
        throw new Error(response.error || 'Verification failed')
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message)
      } else {
        setError('Verification failed')
      }
      throw error // Re-throw so VerificationCodeForm's catch block runs
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <MfaPageContainer>
        <VerificationCodeForm
          onVerify={handleVerify}
          onResend={handleMfaChallengeResend}
          isLoading={isLoading}
          errorMessage={error}
          onErrorReset={() => setError('')}
          analyticsFlowName='Login'
          analyticsMethodName={mfaFactor}
        >
          <MfaCodePageText>{mfaCodeText(mfaFactor, mfaHint)}</MfaCodePageText>
        </VerificationCodeForm>
      </MfaPageContainer>
      {error ? (
        <HelpText>
          If you need to update your contact details, please call us on <b>1300 36 36 88</b>
        </HelpText>
      ) : null}
    </>
  )
}

const MfaPageContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: `${spacing.bigx1} ${spacing.md}`,
  fontFamily: font.family.primary,

  h1: {
    fontFamily: font.family.primary,
    ...font.size.xl4,
    fontWeight: font.weight.semibold,
    alignSelf: 'left',
    padding: 0,
    color: hexColors.black,
    textTransform: 'capitalize',
  },
})
